import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TotemMark } from '@/shared/brand/TotemLogo';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';

export interface CrudColumn<T> {
  key: string;
  header_key: string;
  /** Render a cell value. Defaults to identity. */
  render: (row: T) => React.ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
}

export interface CrudTableProps<T> {
  rows: ReadonlyArray<T>;
  columns: ReadonlyArray<CrudColumn<T>>;
  loading?: boolean;
  empty_label_key?: string;
  page: { total: number; offset: number; limit: number; has_more: boolean };
  onPageChange: (next_offset: number) => void;
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  /** Optional toolbar (search, filters, primary action) rendered above the table. */
  toolbar?: React.ReactNode;
}

/**
 * Generic table for paginated catalog lists. Columns define render fns; the page
 * controls call back with the next offset. No client-side filtering — everything
 * is server-driven (rule §14).
 */
export function CrudTable<T>({
  rows,
  columns,
  loading,
  empty_label_key = 'crud.empty',
  page,
  onPageChange,
  onRowClick,
  rowKey,
  toolbar,
}: CrudTableProps<T>): JSX.Element {
  const { t } = useTranslation();
  const has_prev = page.offset > 0;
  const next_offset = page.offset + page.limit;
  const showing_from = rows.length === 0 ? 0 : page.offset + 1;
  const showing_to = page.offset + rows.length;

  return (
    <div className="space-y-3">
      {toolbar ? <div className="flex flex-wrap items-center gap-2">{toolbar}</div> : null}

      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground backdrop-blur">
            <tr className="border-b border-border">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-4 py-3 font-bold',
                    col.align === 'right' && 'text-right tabular-nums',
                    col.align === 'center' && 'text-center',
                    !col.align && 'text-left',
                    col.width,
                  )}
                >
                  {t(col.header_key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={i} className="border-b border-border/60 last:border-0">
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-3">
                      <Skeleton className={cn('h-4', col.align === 'right' ? 'ml-auto w-16' : 'w-24')} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-14">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <TotemMark className="h-10 w-auto text-muted-foreground/40" aria-hidden />
                    <span className="text-sm text-muted-foreground">{t(empty_label_key)}</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={cn(
                    'border-b border-border/60 transition-colors last:border-0',
                    onRowClick && 'cursor-pointer hover:bg-secondary/50',
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        'px-4 py-3 align-middle',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.align === 'center' && 'text-center',
                        col.className,
                      )}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {t('crud.pagination_label', {
            from: showing_from,
            to: showing_to,
            total: page.total,
          })}
        </span>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(Math.max(0, page.offset - page.limit))}
            disabled={!has_prev || loading === true}
            aria-label={t('crud.prev_page')}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(next_offset)}
            disabled={!page.has_more || loading === true}
            aria-label={t('crud.next_page')}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}
